import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, Plus, Search, Loader2 } from "lucide-react";
import {
  apiV1SeoBrandTermsGet,
  apiV1SeoBrandTermsPut,
} from "@/backend-sdk/client/sdk.gen";
import type { SeoBrandTermsInUse } from "@/backend-sdk/client/types.gen";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BrandTagsInputProps {
  customerUuid?: string;
  /** Chamado quando a lista **gravada** muda, para a página recarregar os recortes. */
  onSaved: (emVigor: SeoBrandTermsInUse) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Componente de input de tags para termos de marca.
 *
 * <auditoria-18> Os termos moram no servidor, em `seo_brand_terms`, e não mais no
 * `localStorage` do navegador. A diferença é o item 18 da auditoria da Magnata: enquanto a
 * lista era do navegador, quem abrisse a loja de outra máquina via "Nenhum termo
 * configurado" e recebia um recorte deduzido — deduzido de dois jeitos diferentes, um aqui
 * e outro no servidor, com números diferentes no mesmo card.
 *
 * O componente lê e grava a lista inteira: cada mudança é um `PUT` que substitui o que
 * estava lá, do mesmo jeito que `PUT /api/v1/campaign-filters` faz com os filtros de
 * campanha. A resposta traz de volta o que **ficou gravado** (aparado, minusculizado e sem
 * repetido) e qual filtro passou a valer, que é o que o rodapé mostra.
 */
const BrandTagsInput: React.FC<BrandTagsInputProps> = ({
  customerUuid,
  onSaved,
  placeholder = "Digite um termo de marca e pressione Enter...",
  className,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [emVigor, setEmVigor] = useState<SeoBrandTermsInUse | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Carregar os termos da loja ao montar, e a cada troca de conta
  useEffect(() => {
    if (!customerUuid) return;
    let ativo = true;

    const carregar = async () => {
      setCarregando(true);
      setErro(null);
      try {
        const { data } = await apiV1SeoBrandTermsGet({
          query: { customer_uuid: customerUuid },
          throwOnError: true,
        });
        if (!ativo) return;
        setTags(data.terms);
        setEmVigor(data.in_use);
      } catch {
        if (!ativo) return;
        setErro("Não foi possível carregar os termos de marca.");
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    void carregar();
    return () => {
      ativo = false;
    };
  }, [customerUuid]);

  /**
   * Grava a lista inteira e adota o que voltou.
   *
   * A tela é atualizada **de forma otimista** antes da resposta, para que a tag apareça no
   * instante do Enter; o que a resposta traz substitui isso, porque o servidor apara,
   * minusculiza e remove repetido, e é o gravado que a próxima leitura vai devolver. Numa
   * falha, a lista volta ao que estava — sem isso, a tela mostraria uma tag que não existe.
   */
  const salvar = useCallback(
    async (novas: string[]) => {
      if (!customerUuid) return;
      const anteriores = tags;
      setTags(novas);
      setSalvando(true);
      setErro(null);
      try {
        const { data } = await apiV1SeoBrandTermsPut({
          query: { customer_uuid: customerUuid },
          body: { terms: novas },
          throwOnError: true,
        });
        setTags(data.terms);
        setEmVigor(data.in_use);
        onSaved(data.in_use);
      } catch {
        setTags(anteriores);
        setErro("Não foi possível salvar os termos de marca.");
      } finally {
        setSalvando(false);
      }
    },
    [customerUuid, tags, onSaved],
  );

  const addTag = useCallback(
    (value: string) => {
      const trimmed = value.trim().toLowerCase();
      if (!trimmed) return;

      setInputValue("");
      // Evitar duplicatas
      if (tags.includes(trimmed)) return;
      void salvar([...tags, trimmed]);
    },
    [tags, salvar],
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      void salvar(tags.filter((tag) => tag !== tagToRemove));
    },
    [tags, salvar],
  );

  const clearAllTags = useCallback(() => {
    void salvar([]);
  }, [salvar]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      // Remover última tag se input está vazio
      removeTag(tags[tags.length - 1]);
    }
  };

  // Tratar colagem de múltiplos termos
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text");
    // Se contém vírgula, adicionar múltiplos termos
    if (pastedText.includes(",")) {
      e.preventDefault();
      const terms = pastedText
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      const uniqueTerms = terms.filter((t) => !tags.includes(t));
      if (uniqueTerms.length > 0) void salvar([...tags, ...uniqueTerms]);
    }
  };

  /**
   * O rodapé, que é onde o item 18 tem efeito visível.
   *
   * Sem termo configurado, ele não diz apenas "nenhum": ele diz **o que está filtrando no
   * lugar**. A tela auditada mostrava "Nenhum termo configurado" enquanto os cards de marca
   * exibiam números, e não havia como saber sobre o que aqueles números eram.
   */
  const resumo = (): string => {
    if (carregando) return "Carregando os termos da loja…";
    if (tags.length > 0) {
      return `${tags.length} termo${tags.length > 1 ? "s" : ""} configurado${tags.length > 1 ? "s" : ""}`;
    }
    if (emVigor?.source === "derived" && emVigor.terms.length > 0) {
      return `Nenhum termo configurado — filtrando por "${emVigor.terms.join('", "')}", deduzido do seu domínio.`;
    }
    return "Nenhum termo configurado. Digite um termo e pressione Enter.";
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-wrap gap-2 min-h-[38px] p-2 border rounded-md bg-background focus-within:ring-1 focus-within:ring-ring">
        {/* Tags renderizadas */}
        {tags.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="flex items-center gap-1 px-2 py-1 text-sm"
          >
            <Search className="h-3 w-3 mr-0.5 opacity-60" />
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              disabled={salvando}
              className="ml-1 rounded-full hover:bg-muted p-0.5 transition-colors disabled:opacity-50"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}

        {/* Input para novo termo */}
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={carregando}
          placeholder={tags.length === 0 ? placeholder : "Adicionar termo..."}
          className="flex-1 min-w-[150px] border-0 shadow-none focus-visible:ring-0 p-0 h-7"
        />
      </div>

      {/* Controles */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          {salvando && <Loader2 className="h-3 w-3 animate-spin" />}
          {erro ?? resumo()}
        </div>

        <div className="flex gap-2">
          {inputValue && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => addTag(inputValue)}
              disabled={salvando}
              className="h-7 px-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              Adicionar
            </Button>
          )}
          {tags.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={clearAllTags}
              disabled={salvando}
              className="h-7 px-2 text-muted-foreground hover:text-destructive"
            >
              Limpar todos
            </Button>
          )}
        </div>
      </div>

      {/* Sugestões de termos */}
      {tags.length === 0 && !carregando && (
        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
          <strong>Dica:</strong> Os termos de marca separam a busca pela sua
          loja da busca genérica: eles filtram os cards "Clicks Marca" e
          "Impressões Marca", a tabela de palavras-chave da aba MARCA e os
          gráficos por dispositivo dela. Configure o nome da loja e as variações
          (ex: "monte leste", "monteleste", "monte"). Eles ficam salvos na loja,
          e valem para toda a equipe.
        </div>
      )}
    </div>
  );
};

export default BrandTagsInput;
